/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import * as yup from 'yup';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  Heading4,
  Spinner,
  TextM,
} from 'app/components';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import { useProductsService } from 'modules/products/services/hooks';
import { StepContentButtonsArea } from '../styles';
import { InputItem, VariablesArea } from './styles';
import { formatDecimalField } from 'app/helpers';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { formatDateForBackend } from 'app/helpers/dateHelpers';
import { IApplicationCustomVariables } from 'app/interfaces/customVariablesInterfaces';
import { CustomSelect, CustomInput, CalendarInput } from 'app/components';

const VariablesStep = () => {
  const [customVariablesList, setCustomVariablesList] = useState<
    IApplicationCustomVariables[] | undefined
  >([]);
  const [validationSchemaObj, setValidationSchemaObj] = useState({});
  const [variablesFormSchema, setVariablesFormSchema] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<any>();
  const { getCustomVariables } = useProductsService();
  const {
    product: productData,
    isClonning,
    onBack,
    onForward,
    updateCustomVariables,
    updateHasCustomVariables,
  } = useContext(ApplicationRequestContext);
  const requiredField = 'Campo obrigatório';

  const handleCreateValidationSchema = (
    variables: IApplicationCustomVariables[] | undefined,
  ) => {
    const obj = {};
    if (variables && variables?.length > 0) {
      variables?.map((variable) => {
        Object.assign(obj, {
          [`${variable.id}`]:
            variable.type === 'BOOLEAN'
              ? variable.type === 'BOOLEAN' && variable.required
                ? yup
                    .object({
                      label: yup.string(),
                      value: yup.string(),
                    })
                    .nullable()
                    .test({
                      name: `${variable.name} field`,
                      test: (value: any, { createError }) => {
                        return !value ||
                          value?.value === undefined ||
                          value?.value === ''
                          ? createError({
                              message: 'Campo obrigatório',
                              path: `${variable.id}`,
                            })
                          : true;
                      },
                    })
                : yup
                    .object({
                      label: yup.string().nullable(),
                      value: yup.string().nullable(),
                    })
                    .nullable()
              : variable.type === 'NUMBER'
              ? variable.type === 'NUMBER' && variable.required
                ? yup.string().required(requiredField)
                : yup.string()
              : variable.type === 'TEXT'
              ? variable.required
                ? yup.string().required(requiredField).min(1)
                : yup.string()
              : variable.type === 'DATE'
              ? variable.type === 'DATE' && variable.required
                ? yup.string().nullable().required(requiredField)
                : yup.string().nullable()
              : yup.string(),
        });
        return true;
      });
    }

    setValidationSchemaObj(obj);
  };

  const handleGetVariables = async () => {
    setLoading(true);
    const customVariableRes = await getCustomVariables(productData?.id || '');

    const applicationCustomVariables = customVariableRes?.application;

    const initialValuesObj: { [key: string]: string | boolean } = {};

    applicationCustomVariables?.map((item) => {
      const value =
        isClonning &&
        isClonning.custom_variables[item.name?.replace('custom_variables.', '')]
          ? isClonning.custom_variables[
              item.name?.replace('custom_variables.', '')
            ]
          : item.type === 'BOOLEAN'
          ? null
          : '';
      Object.assign(initialValuesObj, { [item.id]: value });

      return true;
    });

    setInitialValues(initialValuesObj);

    handleCreateValidationSchema(applicationCustomVariables);
    setCustomVariablesList(applicationCustomVariables);
    setLoading(false);

    if (applicationCustomVariables?.length === 0) {
      updateHasCustomVariables(false);
    } else {
      updateHasCustomVariables(true);
    }
  };

  useEffect(() => {
    if (productData) {
      handleGetVariables();
    }
  }, [productData]);

  useEffect(() => {
    if (validationSchemaObj) {
      setVariablesFormSchema(yup.object(validationSchemaObj).required());
    }
  }, [validationSchemaObj]);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: variablesFormSchema,
    onSubmit(values) {
      const variablesValuesKeys = Object.keys(values);

      const parsedVariablesValues = variablesValuesKeys.map((key) => {
        const currentVar: IApplicationCustomVariables | undefined =
          customVariablesList?.find((i) => i.id === key);
        return {
          name: currentVar?.name?.replace('custom_variables.', ''),
          value:
            currentVar?.type === 'NUMBER'
              ? formatDecimalField(values[key]).replace(',', '.')
              : currentVar?.type === 'DATE'
              ? values[key] &&
                format(formatDateForBackend(values[key]), 'yyyy-MM-dd')
              : currentVar?.type === 'BOOLEAN'
              ? values[key]?.value
              : values[key],
          id: key,
        };
      });

      updateCustomVariables(parsedVariablesValues);
      onForward();
    },
  });

  const handleInputChange = useCallback(
    (
      evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      variable: IApplicationCustomVariables,
    ) => {
      if (variable.type === 'NUMBER') {
        formik.setFieldValue(
          `${evt.target.name}`,
          formatDecimalField(evt.target.value),
        );
        return;
      }
      formik.setFieldValue(`${evt.target.name}`, evt.target.value);
    },
    [],
  );

  const handleInputCalendarChange = useCallback(
    (value: Date | null, variable: IApplicationCustomVariables) => {
      formik.setFieldValue(`${variable.id}`, value);
    },
    [],
  );

  return (
    <BoxSection>
      <Heading4>Quer incluir algum campo adicional?</Heading4>
      <VariablesArea>
        {loading ? (
          <Spinner />
        ) : customVariablesList && customVariablesList.length > 0 ? (
          <>
            {customVariablesList.map((variable) => {
              return (
                <InputItem key={variable.id}>
                  {variable.type === 'BOOLEAN' ? (
                    <CustomSelect
                      handleInputChange={formik.handleChange}
                      name={variable.id}
                      options={[
                        { value: false, label: 'Não' },
                        { value: true, label: 'Sim' },
                        { value: '', label: 'Vazio' },
                      ]}
                      labelValue={variable.display_name}
                      value={{
                        value: formik.values?.[`${variable.id}`]?.value,
                        label: formik.values?.[`${variable.id}`]?.label,
                      }}
                      placeholder="Definir valor"
                      validationError={
                        formik.touched?.[`${variable.id}`]
                          ? formik.errors?.[`${variable.id}`]?.toString()
                          : ''
                      }
                    />
                  ) : variable.type === 'DATE' ? (
                    <CalendarInput
                      name={variable.name}
                      labelValue={variable.display_name}
                      placeholder="00/00/0000"
                      value={formik.values?.[`${variable.id}`]}
                      onChange={(e) => handleInputCalendarChange(e, variable)}
                      validationError={
                        formik.touched?.[`${variable.id}`]
                          ? formik.errors?.[`${variable.id}`]?.toString()
                          : ''
                      }
                    />
                  ) : (
                    <CustomInput
                      handleInputChange={(e) => handleInputChange(e, variable)}
                      name={variable.id}
                      labelValue={variable.display_name}
                      value={formik.values?.[`${variable.id}`] || ''}
                      placeholder={
                        variable.type === 'NUMBER'
                          ? 'Digitar número'
                          : 'Digitar informação'
                      }
                      validationError={
                        formik.touched?.[`${variable.id}`]
                          ? formik.errors?.[`${variable.id}`]?.toString()
                          : ''
                      }
                    />
                  )}
                </InputItem>
              );
            })}
          </>
        ) : (
          <TextM
            style={{
              width: '100%',
              textAlign: 'center',
              color: '#6F6B82',
            }}
          >
            Nenhum campo adicional disponível para este produto.
          </TextM>
        )}
      </VariablesArea>
      <StepContentButtonsArea>
        <ButtonDefault margin={true} onClick={onBack}>
          <ArrowBack />
        </ButtonDefault>
        <ButtonPrimary onClick={formik.submitForm}>Avançar</ButtonPrimary>
      </StepContentButtonsArea>
    </BoxSection>
  );
};

export default VariablesStep;
